import { html, css, unsafeCSS, nothing } from "lit";
import type { TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { localized } from "@lit/localize";

import "@wh-web-front-end/wh-themes/dist/web/walgreens/light.css";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-card";
import "../../elements/web-kiosk-home-link";
import type { AboutContent, AboutCard } from "../../types/content";
import { WebKioskPageElement } from "../web-kiosk-page";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-about": WebKioskAboutElement;
  }
}

@localized()
@customElement("web-kiosk-about")
export class WebKioskAboutElement extends WebKioskPageElement {
  @state() content: AboutContent | undefined = undefined;

  filterCards(cards?: AboutCard[]): AboutCard[] {
    if (!cards) {
      return [];
    }

    return cards;
  }

  renderCards(cards?: AboutCard[]) {
    if (!cards) {
      return nothing;
    }

    return this.filterCards(cards).map((card) => {
      const { heading, description, img, link } = card;

      return html`
        <a href=${this.createRelativeLink(link)}>
          <wh-card flat size="large">
            <section class="card-inner">
              <img src=${img} alt=${heading} />
              <h2>${heading}</h2>
              <p>${description}</p>
            </section>
          </wh-card>
        </a>
      `;
    });
  }

  render(): TemplateResult {
    return html`<section class="page-body">
      <div class="body-inner">
        <web-kiosk-home-link
          link=${this.createRelativeLink("home")}
        ></web-kiosk-home-link>
        <div class="body-content">
          <section name="about-text">
            <h1>${this.content?.heading}</h1>

            <p>${this.content?.description}</p>
          </section>

          <section name="cards">
            ${this.renderCards(this.content?.cards)}
          </section>
        </div>
      </div>
    </section>`;
  }

  static styles = css`
    :host {
      --web-kiosk-bg: #c1e8ff;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-text-color: #323264;
    }

    .page-body {
      height: 100%;
      /* stylelint-disable-next-line declaration-colon-space-after */
      background: ${unsafeCSS(`
        url("assets/about/bg/person.svg") left 3% bottom no-repeat,
        url("assets/about/bg/swirl.svg") center bottom 14% no-repeat,
        url("assets/shared/bg/city.svg") right bottom -7% no-repeat,
        var(--web-kiosk-bg, hotpink);
      `)};
      background-size: calc(100vmin - 485px), contain, 40%;
    }

    @media only screen and (width <= 1800px) {
      .page-body {
        /* stylelint-disable-next-line declaration-colon-space-after */
        background: ${unsafeCSS(`
        url("assets/shared/bg/city.svg") right bottom -7% no-repeat,
        var(--web-kiosk-bg, hotpink);
      `)};
        background-size: 40%;
      }
    }

    a {
      text-decoration: none;
    }

    .body-inner {
      padding: 48px 56px;
    }

    .body-content {
      display: flex;
      gap: 210px;
    }

    section[name="about-text"] {
      width: 38%;
      flex: 1 0 38%;
      margin-left: 8px;
    }

    section[name="about-text"] h1 {
      font-size: 64px;
      font-weight: 700;
      line-height: 72px;
      margin: 52px 0 40px;
    }

    a,
    section[name="about-text"] :is(h1, p),
    wh-card h2 {
      color: var(--web-kiosk-text-color, hotpink);
    }

    section[name="cards"] {
      display: flex;
      flex: 1 1 0;
      gap: 32px;
      margin: 48px 24px 0 0;
    }

    wh-card {
      width: fit-content;
      text-align: center;
    }

    wh-card h2 {
      font-size: 32px;
      font-weight: 700;
      line-height: 38px;
      margin: 0 28px 10px;
    }

    wh-card p {
      font-size: 26px;
      font-weight: 400;
      line-height: 40px;
      margin: 0 0 12px;
    }

    section[name="about-text"] p {
      font-size: 32px;
      line-height: 52px;
    }

    .card-inner {
      min-height: 442px;
      min-width: 376px;
      padding: 32px 0 54px;
    }
  `;
}
